// Reduces values to what is safe to send to Bugsnag and Mixpanel. Fields and hosts are allow-listed, so one nobody
// thought about stays out by default. A route on our own hosts can't be: normalizePath masks what looks variable.

type SafeValue = string | number | boolean;

interface NonErrorEvent {
  addMetadata(section: string, values: Record<string, unknown>): void;
  clearMetadata(section: string): void;
  getMetadata(section: string): unknown;
  originalError?: unknown;
}

const MASK = 'xxx';
// Reads like E_AUTH or QCS-0500. On an object that isn't otherwise an error, `code` could be a one-time passcode.
const ERROR_CODE = /^[A-Z][A-Z\d]*(?:[_-][A-Z\d]+)+$/;
const SAFE_CODE = /^[\w.-]{1,64}$/;
const SAFE_HOST = /^[\w.-]+(?::\d+)?$/;
const SAFE_NAME = /^[A-Za-z][\w.[\]-]{0,63}$/;
const SAFE_NUMBER_KEY = /(?:index|status)$/i;
// The strings call sites pass to `assert` that are safe to keep. An error's own name and code are read separately.
const SAFE_STRING_KEYS = new Set(['id', 'key', 'loginId']);
// Only our routes are worth keeping. Anywhere else a path can hold anything, like the file name in an upload key, which
// looks just like the static part of a route.
const OWN_HOST = /(?:^|\.)(?:localhost|quicken\.com|quickencs\.com|simplifimoney\.com)(?::\d+)?$/i;
// Bugsnag copies a reported value that isn't an Error into a tab named after whatever reported it. plugin-react's is 1.
const NON_ERROR_TABS = ['1', 'ErrorUtils globalHandler', 'notify()', 'promise rejection tracking', 'window onerror'];

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const getSafeCode = (value: unknown): string | undefined =>
  typeof value === 'string' && SAFE_CODE.test(value) ? value : undefined;

export const getSafeName = (value: unknown): string | undefined =>
  typeof value === 'string' && SAFE_NAME.test(value) ? value : undefined;

// Encoded text, emails and base64 have other characters, tickers and VINs have no lowercase, and long values with
// digits are ids or tokens. Lowercase words stay: they're the static parts of a route.
const isVariablePathSegment = (segment: string): boolean =>
  segment !== '' &&
  (/[^\w.~-]/.test(segment) || !/[a-z]/.test(segment) || (segment.length >= 16 && /\d/.test(segment)));

/** The shape of a route. Given the host it came from, only if that host is ours. */
export const normalizePath = (path: string, host?: string): string => {
  if (host !== undefined && !OWN_HOST.test(host)) return `/${MASK}`;
  const segments = path.split('/');
  // A holding's page is named after it, and that name can be one plain word.
  const isHoldingPage = segments[1] === 'investing';
  return segments
    .map((segment, index) =>
      isVariablePathSegment(segment) || (isHoldingPage && index > 1 && segment !== '') ? MASK : segment,
    )
    .join('/');
};

// Keeps names and drops values. A second ? is treated as &, because malformed URLs carry tokens there too, and a name
// with no value is more likely a token than a name.
const scrubParams = (params: string): string =>
  params
    .split(/[&?]/)
    .map((param) => {
      const [name, value] = param.split('=');
      return value && getSafeName(name) ? `${name}=${MASK}` : MASK;
    })
    .join('&');

const URL_PARTS = /^(?:([a-z][a-z\d+.-]*:)?\/\/(?:[^/?#]*@)?([^/?#]*))?([^?#]*)(?:\?([^#]*))?(?:#(.*))?$/is;

/**
 * The origin of a URL, the shape of its route if the host is ours, and parameter names but no values. Never throws, and
 * works on relative URLs.
 */
export const scrubUrl = (url: unknown): string => {
  if (typeof url !== 'string') return '';
  // file: and blob: URLs hold device paths and have no useful route. A blob: URL has no // after its scheme.
  if (/^blob:/i.test(url)) return 'blob:';
  const [, scheme = '', host, path = '', query, hash] = url.match(URL_PARTS) ?? [];
  if (scheme && !/^https?:$/i.test(scheme)) return scheme;
  const origin = host === undefined ? '' : `${scheme}//${SAFE_HOST.test(host) ? host : MASK}`;
  const search = query ? `?${scrubParams(query)}` : '';
  const fragment = hash ? `#${scrubParams(hash)}` : '';
  return `${origin}${normalizePath(path, host)}${search}${fragment}`;
};

/**
 * The fields of an object that are safe to report: booleans, numbers under an index or status key, and short strings
 * under a key known to hold an id, plus the name and code of an error.
 */
export const getSafeDetails = (value: unknown): Record<string, SafeValue> | undefined => {
  if (!isRecord(value)) return undefined;
  const details: Record<string, SafeValue> = {};
  try {
    for (const key of Object.keys(value)) {
      const field = value[key];
      if (!getSafeName(key)) continue;
      if (typeof field === 'boolean' || (typeof field === 'number' && SAFE_NUMBER_KEY.test(key))) {
        details[key] = field;
      } else if (SAFE_STRING_KEYS.has(key) && typeof field === 'string' && getSafeCode(field)) {
        details[key] = field;
      }
    }
    // An Error keeps its name off its own keys. On anything else, `name` is as likely an account's as an error's.
    const isErrorLike = typeof value.message === 'string';
    const name = isErrorLike ? getSafeCode(value.name) : undefined;
    const code = getSafeCode(value.code);
    if (name) details.name = name;
    if (code && (isErrorLike ? /[A-Za-z]/.test(code) : ERROR_CODE.test(code))) details.code = code;
  } catch {
    // An exotic getter threw. Report what was read so far.
  }
  return Object.keys(details).length ? details : undefined;
};

/** Describes what `assert` was handed, for a message or a report context, where redaction can't reach it. */
export const describeValue = (value: unknown): string => {
  // JSON.parse quotes its input in the message.
  if (value instanceof SyntaxError) return value.name;
  if (isRecord(value) && !(value instanceof Error)) {
    const { code, name } = getSafeDetails(value) ?? {};
    return [name, code].filter(Boolean).join(':') || 'Object';
  }
  return String(value);
};

/** Leaves only safe fields in the tabs Bugsnag copies a non-Error into. The report's message points at those tabs. */
export const scrubNonErrorTabs = (event: NonErrorEvent): void => {
  NON_ERROR_TABS.forEach((tab) => {
    if (!event.getMetadata(tab)) return;
    event.clearMetadata(tab);
    const details = getSafeDetails(event.originalError);
    if (details) event.addMetadata(tab, details);
  });
  event.clearMetadata('error cause');
};
